<template>
  <div
      id="header-container-pub"
      :class="{
      'light': !['Home', 'Blog'].includes($route.name),
      'dark': ['Home', 'Blog'].includes($route.name),
      'header-absolute': ['Home', 'Blog'].includes($route.name)
    }"
  >
    <div class="container-fluid">
      <div
          v-if="showBuildInfo"
          id="app-env-text"
          v-tooltip.bottom="buildConfigTooltipOptions"
      >
        {{ appEnv }}
      </div>

      <nav class="navbar navbar-expand-lg">
        <img
            id="brand-logo"
            src="@/assets/settle_estonia.svg"
            alt="Brand logo"
        />

        <Button
            class="navbar-toggler"
            icon="pi pi-bars"
            variant="text"
            rounded
            @click="mobileMenuOpen = !mobileMenuOpen"
        />

        <div
            id="nav-collapse"
            class="navbar-collapse"
            :class="{ collapse: true, show: mobileMenuOpen }"
        >
          <ul class="navbar-nav main-menu-navbar">
            <template v-if="auth.isAuthenticated()">
              <li class="nav-item dropdown">
                <Button class="nav-link dropdown-toggle px-0 py-2" @click.prevent="infoMenuRef?.toggle($event)" variant="link">
                  {{ t("SETTLE_ESTONIA_INFO") }}
                </Button>

                <Menu ref="infoMenuRef" :model="infoMenuItems" :popup="true"/>
              </li>

              <li v-if="canAccessGroups" class="nav-item">
                <router-link to="/groups" class="nav-link">
                  {{ t("TRAINING_GROUPS") }}
                </router-link>
              </li>
              <li v-if="canAccessSearch" class="nav-item">
                <router-link to="/search" class="nav-link">
                  {{ t("SEARCH_QUERY") }}
                </router-link>
              </li>
              <li v-if="canAccessModules" class="nav-item">
                <router-link to="/modules" class="nav-link">
                  {{ t("MODULES") }}
                </router-link>
              </li>
              <li v-if="canAccessSchools" class="nav-item">
                <router-link to="/schools" class="nav-link">
                  {{ t("SCHOOLS") }}
                </router-link>
              </li>
              <li v-if="canAccessStatistics" class="nav-item">
                <router-link to="/statistics" class="nav-link">
                  {{ t("STATISTICS") }}
                </router-link>
              </li>
              <li v-if="canAccessInstitutionDiplomas" class="nav-item">
                <router-link to="/institution-diplomas" class="nav-link">
                  {{ t("DOCUMENTS") }}
                </router-link>
              </li>
            </template>

            <template v-else>
              <li class="nav-item">
                <router-link to="/" class="nav-link">
                  {{ t("HOME") }}
                </router-link>
              </li>
              <li class="nav-item">
                <router-link to="/moving-to-estonia" class="nav-link">
                  {{ t("MOVING_TO_ESTONIA") }}
                </router-link>
              </li>
              <li class="nav-item">
                <router-link to="/programme" class="nav-link">
                  {{ t("PROGRAMME") }}
                </router-link>
              </li>
              <li class="nav-item">
                <router-link to="/adaptation" class="nav-link">
                  {{ t("ADAPTATION") }}
                </router-link>
              </li>
            </template>
          </ul>

          <ul class="navbar-nav ms-auto">
            <li v-if="!auth.isAuthenticated()" class="nav-item">
              <router-link to="/login" class="nav-link">
                {{ t("LOG_IN") }}
              </router-link>
            </li>

            <LocaleSwitcher/>

            <li v-if="auth.isAuthenticated()" class="nav-item dropdown" id="user-dropdown">
              <Button class="nav-link dropdown-toggle px-0 py-2" @click.prevent="userMenuRef?.toggle($event)" variant="link">
                {{ auth.getDisplayName() }}
              </Button>

              <Menu ref="userMenuRef" :model="userMenuItems" :popup="true"/>
            </li>
          </ul>
        </div>
      </nav>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import LocaleSwitcher from "@/components/LocaleSwitcher.vue";
import {injectAuth, injectAxios} from "@/util/inject";
import {useI18n} from "vue-i18n";
import {Role} from "@/auth/oauth";
import {AuthMethod} from "@/model/auth-method";
import {useRouter} from "vue-router";

const axios = injectAxios();
const auth = injectAuth();

const {t} = useI18n();
const router = useRouter();

const mobileMenuOpen = ref(false);
const infoMenuRef = ref(null);
const userMenuRef = ref(null);

const backendBuildConfig = ref(null);
const appEnv = ref(import.meta.env.VITE_APP_ENV);
const appVersion = ref(import.meta.env.VITE_APP_VERSION);
const appRevision = ref(import.meta.env.VITE_BUILD_REVISION);

const showBuildInfo = computed(() => {
  return ["local", "dev", "test"].includes(appEnv.value);
});

const buildConfigTooltipValue = computed(() => {
  let result = `Frontend: ${appVersion.value}, revision: ${appRevision.value}`;

  if (backendBuildConfig.value) {
    result += `\nBackend: ${backendBuildConfig.value.version}, revision: ${backendBuildConfig.value.revision}`;
  }

  return result;
});

const buildConfigTooltipOptions = computed(() => ({
  value: buildConfigTooltipValue.value,
  pt: {
    root: {
      style: 'font-size: 0.875rem; margin: 2rem; max-width: 360px;'
    }
  }
}));

const infoMenuItems = computed(() => [
  {label: t("HOME"), command: () => router.push("/")},
  {label: t("MOVING_TO_ESTONIA"), command: () => router.push("/moving-to-estonia")},
  {label: t("PROGRAMME"), command: () => router.push("/programme")},
  {label: t("ADAPTATION"), command: () => router.push("/adaptation")},
]);

const userMenuItems = computed(() => {
  const items = [];

  if (isPasswordAuth.value) {
    items.push({label: t("CHOOSE_PASSWORD"), command: () => router.push("/account/password")});
  }

  items.push(
      {label: t("MANAGE_PROFILE"), command: () => router.push("/account/profile")},
      {separator: true},
      {label: t("LOG_OUT"), command: () => auth.logout()}
  );

  return items;
});

onMounted(async () => {
  if (showBuildInfo.value) {
    await fetchBackendBuildConfig();
  }
});

const canAccessSearch = computed(() => {
  return auth.hasSomeRole(Role.ADMIN, Role.VIEWER, Role.RVKT_ADMIN);
});

const canAccessModules = computed(() => {
  return auth.hasSomeRole(Role.ADMIN, Role.VIEWER, Role.RVKT_ADMIN, Role.SCHOOL_MANAGER);
});

const canAccessSchools = computed(() => {
  return auth.hasRole(Role.ADMIN);
});

const canAccessGroups = computed(() => {
  return auth.hasSomeRole(Role.ADMIN, Role.VIEWER, Role.RVKT_ADMIN, Role.TRAINEE, Role.TRAINER, Role.SCHOOL_MANAGER);
});

const canAccessStatistics = computed(() => {
  return auth.hasSomeRole(Role.ADMIN, Role.VIEWER, Role.RVKT_ADMIN);
});

const canAccessInstitutionDiplomas = computed(() => {
  return auth.hasSomeRole(Role.ADMIN, Role.VIEWER, Role.RVKT_ADMIN);
});

const isPasswordAuth = computed(() => {
  return auth.getAuthMethod() === AuthMethod.PASSWORD;
});

const fetchBackendBuildConfig = async () => {
  const resp = await axios.get("/ping");
  backendBuildConfig.value = resp.data;
};

watch(() => router.currentRoute.value.path, () => {
  mobileMenuOpen.value = false;
});
</script>

<style lang="scss" scoped>
#header-container-pub {
  #brand-logo {
    margin-top: -0.5rem;
    margin-right: 15px;
  }

  .container-fluid {
    padding-left: 32px;
    padding-right: 32px;
  }
}

:deep(.nav-link) {
  font-size: 18px;
  text-transform: capitalize;

  #header-container-pub.light & {
    color: var(--p-color-blue-600);
  }

  #header-container-pub.dark & {
    color: var(--p-color-white-0);
  }
}

:deep(.dropdown-item) {
  font-size: 18px;
  color: var(--p-color-blue-600);

  &:focus {
    background-color: var(--p-color-blue-600);
    color: var(--p-color-white-0);
  }
}

:deep(.navbar) {
  padding-left: 0;
  padding-right: 0;

  .nav-item {
    margin: 0 0.75rem;

    & > a {
      white-space: nowrap;
    }
  }

  #user-dropdown > .user-dropdown-toggle {
    max-width: 280px;
    padding-right: 1rem;
    overflow-x: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    position: relative;
    display: inline-block;

    &::after {
      position: absolute;
      right: 0;
      top: 1.25rem;
    }
  }
}

.navbar-collapse, .navbar-nav {
  align-self: start;
}

:deep(.navbar-toggler) {
  margin-right: 0.5rem;
  margin-top: 1.5rem;
  min-width: 2.5rem;
  min-height: 2.5rem;

  .p-button-icon {
    font-size: 1.5rem;
  }
}

#header-container-pub :deep(.navbar-toggler) {
  color: var(--p-color-blue-600);
  background-color: var(--p-color-white-0);
  border: 2px solid var(--p-color-blue-600);

  &:hover {
    background-color: var(--p-color-blue-50);
  }
}

@media screen and (max-width: 991px) {
  :deep(.nav-link) {
    #header-container-pub.dark & {
      color: var(--p-color-blue-600);
    }
  }

  #nav-collapse {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    background: var(--p-color-white-0);
    z-index: 15;
    border-bottom-left-radius: 9px;
    border-bottom-right-radius: 9px;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);

    & > ul {
      padding: 2rem;
    }

    & > ul:first-child {
      padding-top: 120px;
    }
  }

  #brand-logo, .navbar-toggler {
    z-index: 16;
  }

  :deep(.navbar-light .navbar-nav .nav-link) {
    color: var(--p-color-blue-600) !important;
  }

  :deep(.navbar) {
    .nav-item {
      margin: 0;

      & > a {
        white-space: break-spaces;
      }
    }
  }
}

:deep(.dropdown-toggle) {
  outline: none;
}

.main-menu-navbar {
  flex-wrap: wrap;
  margin-right: 2rem;
}

.header-absolute {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

#app-env-text {
  position: fixed;
  z-index: 17;
  top: 0.25rem;
  left: -4rem;
  padding: 0.125rem 5rem;
  font-size: 0.75em;
  background: rgba(205, 0, 0, 0.7);
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
  color: #ffffff;
  text-transform: uppercase;
  border: 1px solid #ffffff;
  transform: rotate(-25deg);
  cursor: default;
}
</style>

<style lang="scss">
.build-config-tooltip {
  margin-top: -2.75rem;

  & > .tooltip-inner {
    max-width: unset;
    font-size: 1.1em;
    text-align: left;
  }
}
</style>
